import GridColumn from "@/components/common/grid-column/grid-column";
import css from "./resources-article-block.module.scss";
import GridRow from "@/components/common/grid-row/grid-row";
import imageUrl from "@/sanity/utilities/imageUrl";
import { ArticleData } from "@/sanity/interfaces";
import Image from "@/components/common/image/image";
import { ArticleCategory } from "@/sanity/sanity.types";
import BaseLink from "@/components/common/base-link/base-link";
import Link, { LinkStyle, LinkType } from "@/components/common/link/link";
import ArrowIcon from "@/components/common/arrow-icon/arrow-icon";
import useBreakpoint from "@/hooks/useBreakpoint";
import { ImageLoading } from "@/utilities/imageLoading";
import dynamic from "next/dynamic";

const ResourcesArticleInfo = dynamic(
  () => import("../resources-article-info/resources-article-info"),
  { ssr: false },
);

interface IResourceArticleBlockAutoProps {
  category?: ArticleCategory;
  articles: ArticleData[];
  imageLoading?: ImageLoading;
  reverse?: boolean;
  title?: string;
}

const ResourcesArticleBlockAuto = (props: IResourceArticleBlockAutoProps) => {
  const { category, articles: items, imageLoading, reverse } = props;
  const relatedTitle = props.title ?? "Related Articles";

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <GridRow className="grid h-full grid-cols-2 items-stretch gap-5">
      <GridColumn className="mb-6 md:mb-0">
        <div className={css.resourceArticleBlockHeader}>
          <h2 className="text-[#373A36] md:text-[28px]">
            {category?.title ?? relatedTitle}
          </h2>
          <div className="hidden md:block">
            <Link
              href={`${
                category?.slug?.current
                  ? `/learn/cat/${category?.slug?.current}`
                  : `/learn`
              }`}
              linkType={LinkType.BUTTON}
              linkStyle={LinkStyle.SECONDARY}
              className="!px-0 py-[12px] !underline"
              data-cta-id={"view-all"}
              data-cta-value={category?.slug?.current ?? "view-all-homepage"}
            >
              <span>View All</span>
            </Link>
          </div>
        </div>
      </GridColumn>

      {reverse ? (
        <>
          {items.length >= 2 && (
            <RightColumn data={items.slice(0, 2)} imageLoading={imageLoading} />
          )}
          {items.length >= 3 && (
            <LeftColumn data={items[2]} imageLoading={imageLoading} />
          )}
        </>
      ) : (
        <>
          {items.length >= 1 && (
            <LeftColumn data={items[0]} imageLoading={imageLoading} />
          )}
          {items.length >= 3 && (
            <RightColumn data={items.slice(1, 3)} imageLoading={imageLoading} />
          )}
        </>
      )}

      <GridColumn style={{ marginBottom: "16px" }}>
        <div className="md:hidden">
          <Link
            href={`${
              category?.slug?.current
                ? `/learn/cat/${category?.slug?.current}`
                : `/learn`
            }`}
            className="inline-flex !w-full items-center justify-center gap-2 rounded-md border-2 border-[#00698F] px-3 py-2 !text-[16px] font-semibold text-[#00698F]"
            data-cta-id={"view-all"}
            data-cta-value={category?.slug?.current ?? "view-all-homepage"}
          >
            View All
            <ArrowIcon />
          </Link>
        </div>
      </GridColumn>
    </GridRow>
  );
};

interface ILeftColumnProps {
  data: ArticleData;
  imageLoading?: ImageLoading;
}

const LeftColumn = (props: ILeftColumnProps) => {
  const { data, imageLoading } = props;
  const { heroImage, title, seo } = data;

  return (
    <GridColumn lg={6}>
      <BaseLink
        href={`/learn/${data.slug?.current ?? ""}`}
        data-cta-id={data?.ctaId ?? ""}
        data-cta-value={data?.ctaValue ?? ""}
      >
        <div
          className={`mb-[10px] flex h-full flex-col justify-between md:mb-0 ${css.resourceArticleBlock}`}
        >
          {heroImage?.asset && (
            <Image
              className={`${css.resourceArticleBlockImage} h-[200px] w-full`}
              src={imageUrl(heroImage.asset).height(200).url()}
              alt={data?.title ?? ""}
              width={2560}
              height={1707}
              loading={imageLoading}
            />
          )}
          <div
            className={`${css.resourceArticleBlockContent} flex flex-1 flex-col justify-between`}
          >
            <h4 className={`${css.resourceArticleBlockTitle} line-clamp-2`}>
              {title}
            </h4>
            {seo?.description && (
              <p
                className={`${css.resourceArticleBlockDescription} line-clamp-2`}
              >
                {seo.description}
              </p>
            )}
            <ResourcesArticleInfo data={data} />
          </div>
        </div>
      </BaseLink>
    </GridColumn>
  );
};

interface IRightColumnProps {
  data: ArticleData[];
  imageLoading?: ImageLoading;
}

const RightColumn = (props: IRightColumnProps) => {
  const { data, imageLoading } = props;
  const { lg } = useBreakpoint();

  return (
    <GridColumn lg={6} className="flex flex-col justify-between gap-5">
      {data.map((article) => (
        <BaseLink
          href={`/learn/${article.slug?.current ?? ""}`}
          key={article._id}
          data-cta-id={article?.ctaId ?? ""}
          data-cta-value={article?.ctaValue ?? ""}
        >
          <div
            className={`${
              lg ? css.resourceArticleBlockCompact : css.resourceArticleBlock
            } flex flex-1 items-center justify-start`}
          >
            <div className={`${css.imageContainer} md:hidden`}>
              {article.heroImage?.asset && (
                <Image
                  className={css.resourceArticleBlockImage}
                  src={imageUrl(article.heroImage.asset).height(160).url()}
                  alt={article.title ?? ""}
                  width={2560}
                  height={1707}
                  loading={imageLoading}
                />
              )}
            </div>
            <div
              className={`${
                lg
                  ? "flex flex-1 flex-col justify-between gap-3"
                  : css.resourceArticleBlockContent
              }`}
            >
              <h4
                className={`${css.resourceArticleBlockTitle} line-clamp-2 pr-5`}
              >
                {article.title}
              </h4>
              {article?.seo?.description && (
                <p
                  className={`${css.resourceArticleBlockDescription} line-clamp-2 flex-grow pr-5`}
                >
                  {article.seo.description}
                </p>
              )}
              <ResourcesArticleInfo
                className={css.resourceBlockArticleBlockCompactInfo}
                data={article}
              />
            </div>

            <div className={`${css.imageContainer} hidden md:block`}>
              {article.heroImage?.asset && (
                <Image
                  className={`${css.resourceArticleBlockImage}`}
                  src={imageUrl(article.heroImage.asset).height(287).url()}
                  alt={article.title ?? ""}
                  width={2560}
                  height={1707}
                  loading={imageLoading}
                />
              )}
            </div>
          </div>
        </BaseLink>
      ))}
    </GridColumn>
  );
};

export default ResourcesArticleBlockAuto;
